import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Stack,
  Autocomplete,
  Divider,
  Alert,
  AlertTitle,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import { useUser } from "../../Context/UserContext";
import DynamicTable from "../Dynamic/DynamicTable";
import DynamicOffcanvas from "../Dynamic/DynamicOffcanvas";
import { toast } from "react-toastify";
import { DeleteApi, GetApi, PostApi, PutApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";

const canModify = (role) => role === "superadmin";
const TYPE_OPTIONS = ["Non-Scheme", "Scheme"];

const now = new Date();
const baseYear =
  now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const currentFY = `${baseYear}-${baseYear + 1}`;
const years = Array.from(
  { length: 10 },
  (_, i) => `${baseYear + i}-${baseYear + i + 1}`,
);

const DepartmentTarget = () => {
  const { token, role, userId } = useUser();
  const [data, setData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [existModal, setExistModal] = useState(false);
  const [existInfo, setExistInfo] = useState(null);

  const [filterDepartment, setFilterDepartment] = useState(null);

  // ✅ Removed useState for filteredData to handle via useMemo for better performance and sorting

  const [form, setForm] = useState({
    department_id: null,
    scheme_type: null,
    scheme_id: null,
    target_quantity: "",
    financial_year: currentFY,
  });

  const [errors, setErrors] = useState({});
  const hasModifyPermission = canModify(role);

  const fetchTargets = async () => {
    try {
      const res = await GetApi(
        `${API_URLS.DepartmentTarget}?user_id=${userId}`,
        token,
      );
      const freshData = res.data || res || [];
      setData(freshData);
      return freshData;
    } catch (err) {
      console.error("Fetch Targets Error:", err);
      return data;
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await GetApi(`${API_URLS.Department}`, token);
      setDepartments(res.data || res || []);
    } catch (err) {
      console.error("Fetch Departments Error:", err);
    }
  };

  const fetchSchemes = async () => {
    try {
      const res = await GetApi(`${API_URLS.Schemes}`, token);
      setSchemes(res.data || res || []);
    } catch (err) {
      console.error("Fetch Schemes Error:", err);
    }
  };

  useEffect(() => {
    fetchTargets();
    fetchDepartments();
    fetchSchemes();
  }, []);

  // ✅ FILTER AND SORT DATA (ASCENDING ORDER BY DEPARTMENT NAME)
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let result = data;
    if (filterDepartment) {
      result = result.filter(
        (row) => Number(row.department_id) === Number(filterDepartment.id),
      );
    }

    // Sort by Department Name Ascending
    return result.sort((a, b) => {
      const nameA = (a.department_name || "").toString().toLowerCase();
      const nameB = (b.department_name || "").toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [data, filterDepartment]);

  // ✅ CALCULATE TOTAL TARGET VALUE
  const totalTargetValue = useMemo(() => {
    return filteredData.reduce(
      (sum, row) => sum + Number(row.target_quantity || 0),
      0,
    );
  }, [filteredData]);

  const getUniqueDepartments = () => {
    const uniqueIds = [...new Set(data.map((row) => row.department_id))];
    return uniqueIds
      .map((id) => departments.find((d) => d.id === id))
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const getExistingForDeptYear = (
    targetData,
    deptId,
    fy,
    excludeEditId = null,
  ) => {
    return targetData.filter((item) => {
      const isEditItem = excludeEditId && item.target_id === excludeEditId;
      return (
        Number(item.department_id) === Number(deptId) &&
        item.financial_year === fy &&
        !isEditItem
      );
    });
  };

  const computeRemainingForData = (
    targetData,
    deptId,
    fy,
    excludeEditId = null,
  ) => {
    if (!deptId || !fy) return { types: TYPE_OPTIONS, schemes: schemes };
    const existing = getExistingForDeptYear(
      targetData,
      deptId,
      fy,
      excludeEditId,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    const hasRemainingSchemes = schemes.some(
      (s) => !usedSchemeIds.includes(Number(s.id)),
    );
    const types = [];
    if (!hasNonScheme) types.push("Non-Scheme");
    if (hasRemainingSchemes) types.push("Scheme");
    const finalTypes = types.length > 0 ? types : TYPE_OPTIONS;
    const remainingSchemes = schemes.filter(
      (s) => !usedSchemeIds.includes(Number(s.id)),
    );
    return { types: finalTypes, schemes: remainingSchemes };
  };

  const availableTypes = useMemo(() => {
    const { types } = computeRemainingForData(
      data,
      form.department_id,
      form.financial_year,
      editId,
    );
    return types;
  }, [form.department_id, form.financial_year, data, editId, schemes]);

  const availableSchemes = useMemo(() => {
    if (
      form.scheme_type !== "Scheme" ||
      !form.department_id ||
      !form.financial_year
    )
      return [];
    const { schemes: remaining } = computeRemainingForData(
      data,
      form.department_id,
      form.financial_year,
      editId,
    );
    return remaining;
  }, [
    form.scheme_type,
    form.department_id,
    form.financial_year,
    data,
    editId,
    schemes,
  ]);

  const hierarchyStatus = useMemo(() => {
    if (!form.department_id || !form.financial_year) return null;
    const existing = getExistingForDeptYear(
      data,
      form.department_id,
      form.financial_year,
      null,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    return {
      nonSchemeSet: hasNonScheme,
      schemesSet: usedSchemeIds.length,
      totalSchemes: schemes.length,
      remainingSchemes: schemes.length - usedSchemeIds.length,
    };
  }, [form.department_id, form.financial_year, data, schemes]);

  useEffect(() => {
    if (form.scheme_type && !availableTypes.includes(form.scheme_type))
      setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
  }, [availableTypes, form.scheme_type]);

  useEffect(() => {
    if (form.scheme_type === "Scheme" && form.scheme_id) {
      if (
        !availableSchemes.some((s) => Number(s.id) === Number(form.scheme_id))
      )
        setForm((prev) => ({ ...prev, scheme_id: null }));
    }
  }, [availableSchemes, form.scheme_type, form.scheme_id]);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
  };

  const resetTypeAndScheme = () => {
    setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.scheme_type;
      delete n.scheme_id;
      return n;
    });
  };

  const validate = () => {
    const tempErrors = {};
    if (!form.department_id) tempErrors.department_id = "Select a department";
    if (!form.scheme_type) tempErrors.scheme_type = "Select a target type";
    if (form.scheme_type === "Scheme" && !form.scheme_id)
      tempErrors.scheme_id = "Select a scheme";
    if (!form.target_quantity || Number(form.target_quantity) <= 0)
      tempErrors.target_quantity = "Enter a valid target quantity";
    if (!form.financial_year)
      tempErrors.financial_year = "Select a financial year";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const duplicate = data.find((item) => {
      if (editId && Number(item.target_id) === Number(editId)) return false;
      if (Number(item.department_id) !== Number(form.department_id))
        return false;
      if (item.financial_year !== form.financial_year) return false;
      if (form.scheme_type === "Scheme")
        return (
          item.scheme_type === "Scheme" &&
          Number(item.scheme_id) === Number(form.scheme_id)
        );
      return item.scheme_type === "Non-Scheme";
    });
    if (duplicate) {
      setExistInfo({
        department:
          departments.find((d) => Number(d.id) === Number(form.department_id))
            ?.name || "Department",
        scheme:
          form.scheme_type === "Scheme"
            ? schemes.find((s) => Number(s.id) === Number(form.scheme_id))
                ?.name || "Scheme"
            : "Non-Scheme",
      });
      setExistModal(true);
      return;
    }
    try {
      const payload = {
        department_id: Number(form.department_id),
        target_quantity: Number(form.target_quantity),
        financial_year: form.financial_year,
        created_by: 1,
        scheme_type: form.scheme_type,
        scheme_id:
          form.scheme_type === "Scheme" ? Number(form.scheme_id) : null,
      };
      if (editId) {
        await PutApi(
          `${API_URLS.DepartmentTarget}/${editId}`,
          {},
          payload,
          token,
        );
        await fetchTargets();
        handleClose();
        toast.success("Target updated successfully!");
      } else {
        await PostApi(`${API_URLS.DepartmentTarget}`, payload, token);
        const freshData = await fetchTargets();
        const { types: remaining } = computeRemainingForData(
          freshData,
          form.department_id,
          form.financial_year,
          null,
        );
        if (remaining.length > 0) {
          setForm((prev) => ({
            ...prev,
            scheme_type: remaining.length === 1 ? remaining[0] : null,
            scheme_id: null,
            target_quantity: "",
          }));
          setErrors({});
          toast.success(
            "Target saved! More targets pending for this department.",
          );
        } else {
          handleClose();
          toast.success("All targets set for this department!");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Request Failed");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.target_id);
    setForm({
      department_id: row.department_id,
      scheme_type: row.scheme_type || (row.scheme_id ? "Scheme" : "Non-Scheme"),
      scheme_id: row.scheme_id || null,
      target_quantity: row.target_quantity,
      financial_year: row.financial_year || currentFY,
    });
    setErrors({});
    setOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await DeleteApi(
        `${API_URLS.DepartmentTarget}/${deleteTarget.target_id}`,
        {},
        token,
      );
      fetchTargets();
      toast.success("Deleted successfully");
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({
      department_id: null,
      scheme_type: null,
      scheme_id: null,
      target_quantity: "",
      financial_year: currentFY,
    });
    setErrors({});
  };

  const columns = [
    { field: "department_name", headerName: "Department" },
    { field: "scheme_type", headerName: "Type" },
    {
      field: "scheme_name",
      headerName: "Scheme",
      render: (row) => row.scheme_name || "—",
    },
    { field: "target_quantity", headerName: "Target" },
    { field: "financial_year", headerName: "Financial Year" },
  ];

  const greenTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "rgb(12, 105, 199)" },
      "&:hover fieldset": { borderColor: "rgb(43, 143, 243)" },
      "&.Mui-focused fieldset": { borderColor: "rgb(43, 143, 243)" },
      "& .MuiInputLabel-root": { color: "rgb(43, 143, 243)" },
      "& .MuiInputLabel-root.Mui-focused": { color: "rgb(43, 143, 243)" },
    },
  };

  return (
    <Box p={2}>
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <FilterListIcon sx={{ color: "#2e7d32" }} />
            <Autocomplete
              options={getUniqueDepartments()}
              getOptionLabel={(option) => option.name}
              value={filterDepartment}
              onChange={(e, newValue) => setFilterDepartment(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              size="small"
              sx={{ minWidth: 280 }}
              clearIcon={<ClearIcon fontSize="small" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Department"
                  placeholder="Search department..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4caf50",
                        borderWidth: 1.5,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2e7d32 !important",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1b5e20",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000000",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#000000",
                    },
                  }}
                />
              )}
            />
          </Stack>

          {hasModifyPermission && (
            <Button
              variant="contained"
              color="success"
              onClick={() => setOpen(true)}
              sx={{ fontWeight: 600 }}
            >
              Set Target
            </Button>
          )}
        </Stack>
      </Paper>

      {/* ✅ TOTAL TARGET VALUE DISPLAY */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #c8e6c9",
          borderRadius: 2,
          bgcolor: "#f1f8e9",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32" }}>
            Total Target Value
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b5e20" }}>
            {totalTargetValue.toLocaleString()}
          </Typography>
        </Stack>
      </Paper>

      <DynamicTable
        data={filteredData}
        columns={columns}
        onEdit={hasModifyPermission ? handleEdit : undefined}
        onDelete={
          hasModifyPermission
            ? (row) => {
                setDeleteTarget(row);
                setDeleteModal(true);
              }
            : undefined
        }
      />

      <Dialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography fontWeight={600}>Confirm Delete</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this target?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            sx={{
              borderColor: "green",
              color: "green",
              "&:hover": {
                borderColor: "darkgreen",
                backgroundColor: "rgba(0,128,0,0.08)",
              },
            }}
            onClick={() => setDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{
              backgroundColor: "green",
              color: "#fff",
              "&:hover": { backgroundColor: "darkgreen" },
            }}
            onClick={confirmDelete}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={existModal}
        onClose={() => setExistModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <Typography fontWeight={600}>Target Already Exists</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Target for <strong>{existInfo?.department}</strong> with{" "}
            <strong>{existInfo?.scheme}</strong> is already set.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExistModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DynamicOffcanvas
        open={open}
        title={editId ? "Edit Target" : "Set Target"}
        onClose={handleClose}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              sx={{
                borderColor: "#4caf50",
                color: "#4caf50",
                "&:hover": {
                  borderColor: "#2e7d32",
                  backgroundColor: "rgba(76, 175, 80, 0.08)",
                },
              }}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
              variant="contained"
              onClick={handleSubmit}
            >
              {editId ? "Update" : "Save Target"}
            </Button>
          </Stack>
        }
      >
        {hierarchyStatus && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              Target Progress
            </AlertTitle>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {hierarchyStatus.nonSchemeSet ? (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2">Non-Scheme</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {hierarchyStatus.remainingSchemes === 0 ? (
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2">
                  Schemes ({hierarchyStatus.schemesSet}/
                  {hierarchyStatus.totalSchemes})
                </Typography>
              </Stack>
            </Stack>
          </Alert>
        )}

        <Grid container spacing={2.5}>
          {/* Department */}
          <Grid item xs={12} width="250px">
            <Autocomplete
              options={departments}
              getOptionLabel={(option) => option.name}
              value={
                departments.find((d) => d.id === form.department_id) || null
              }
              onChange={(e, newValue) => {
                handleChange("department_id", newValue?.id || null);
                resetTypeAndScheme();
              }}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  error={!!errors.department_id}
                  helperText={errors.department_id}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4caf50",
                        borderWidth: 1.5,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2e7d32 !important",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1b5e20",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000000",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                  }}
                />
              )}
            />
          </Grid>

          {/* Financial Year */}
          <Grid item xs={12} width="250px">
            <Autocomplete
              options={years}
              getOptionLabel={(option) => option}
              value={form.financial_year || null}
              onChange={(e, newValue) => {
                handleChange("financial_year", newValue || null);
                resetTypeAndScheme();
              }}
              isOptionEqualToValue={(option, value) => option === value}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Financial Year"
                  error={!!errors.financial_year}
                  helperText={errors.financial_year}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4caf50",
                        borderWidth: 1.5,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2e7d32 !important",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1b5e20",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000000",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Target Type */}
          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableTypes}
              getOptionLabel={(option) => option}
              value={form.scheme_type || null}
              onChange={(e, newValue) => {
                handleChange("scheme_type", newValue || null);
                handleChange("scheme_id", null);
              }}
              isOptionEqualToValue={(option, value) => option === value}
              size="small"
              disabled={!form.department_id || !form.financial_year}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Type"
                  error={!!errors.scheme_type}
                  helperText={
                    errors.scheme_type ||
                    (!form.department_id || !form.financial_year
                      ? "Select department & year first"
                      : "")
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4caf50",
                        borderWidth: 1.5,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#2e7d32 !important",
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1b5e20",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#000000",
                      fontWeight: 500,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                  }}
                />
              )}
            />
          </Grid>

          {/* Scheme */}
          {form.scheme_type === "Scheme" && (
            <Grid item xs={12} width="250px">
              <Autocomplete
                options={availableSchemes}
                getOptionLabel={(option) => option.name}
                value={
                  schemes.find(
                    (s) => Number(s.id) === Number(form.scheme_id),
                  ) || null
                }
                onChange={(e, newValue) =>
                  handleChange("scheme_id", newValue?.id || null)
                }
                isOptionEqualToValue={(option, value) =>
                  Number(option.id) === Number(value?.id)
                }
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Scheme"
                    error={!!errors.scheme_id}
                    helperText={
                      errors.scheme_id ||
                      `${availableSchemes.length} scheme(s) available`
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Target Quantity */}
          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Target Quantity"
              value={form.target_quantity}
              onChange={(e) => handleChange("target_quantity", e.target.value)}
              error={!!errors.target_quantity}
              helperText={errors.target_quantity}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4caf50",
                    borderWidth: 1.5,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2e7d32 !important",
                    borderWidth: 2,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#1b5e20",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": { color: "#000000", fontWeight: 500 },
                "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
              }}
            />
          </Grid>
        </Grid>
      </DynamicOffcanvas>
    </Box>
  );
};
export default DepartmentTarget;

